import { SELECTABLE_ACTOR_MODELS } from "@arti/ai-models-config/selectableActorModels";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { classifyModelRef } from "./model-icons/classify";
import { ModelFamilyIcon, ProviderBadgeIcon } from "./model-icons/icons";

interface ModelPickerProps {
  value: string;
  onChange: (ref: string) => void;
  showTemplateDefault?: boolean;
  /** Label for the empty-value option ("" ref). When neither this nor
   *  showTemplateDefault is set, the picker has no empty option and an empty
   *  value renders as an explicit "Select model…" placeholder — never as a
   *  silently-displayed first model that was not actually saved. */
  noneOptionLabel?: string;
  id?: string;
  className?: string;
}

interface OptionItem {
  label: string;
  ref: string;
}

function buildOptions(
  showTemplateDefault: boolean,
  noneOptionLabel: string | undefined,
): OptionItem[] {
  const base: OptionItem[] = SELECTABLE_ACTOR_MODELS.map((m) => ({
    label: m.label,
    ref: m.ref,
  }));
  const noneLabel =
    noneOptionLabel ?? (showTemplateDefault ? "Template default" : undefined);
  if (noneLabel !== undefined) {
    return [{ label: noneLabel, ref: "" }, ...base];
  }
  return base;
}

/** Stacked glyph: family icon (~18px) with provider sub-badge overlaid bottom-right (~10px) */
function ModelGlyph({
  modelRef,
  size = 18,
}: {
  modelRef: string;
  size?: number;
}) {
  if (!modelRef) {
    // Neutral glyph for "Template default"
    return (
      <span
        className="inline-flex shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-400"
        style={{ fontSize: size * 0.5, height: size, width: size }}
        aria-hidden="true"
      >
        ·
      </span>
    );
  }
  const { family, provider } = classifyModelRef(modelRef);
  const badgeSize = Math.round(size * 0.56);
  return (
    <span
      className="relative inline-flex shrink-0"
      style={{ height: size, width: size }}
      aria-hidden="true"
    >
      <ModelFamilyIcon
        family={family}
        className="h-full w-full text-slate-700 dark:text-slate-300"
      />
      {provider !== "unknown" && (
        <span
          className="absolute bottom-0 right-0 flex items-center justify-center rounded-full bg-white ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700"
          style={{ height: badgeSize, padding: 1, width: badgeSize }}
        >
          <ProviderBadgeIcon
            provider={provider}
            className="h-full w-full text-slate-500 dark:text-slate-400"
          />
        </span>
      )}
    </span>
  );
}

export function ModelPicker({
  value,
  onChange,
  showTemplateDefault = false,
  noneOptionLabel,
  id,
  className,
}: ModelPickerProps) {
  const options = buildOptions(showTemplateDefault, noneOptionLabel);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);

  const listboxId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // No fallback to options[0]: displaying a model the form did not actually
  // hold means the save silently writes something else. Unknown non-empty
  // refs (e.g. a model retired from the catalog) render verbatim, flagged.
  const selectedOption = options.find((o) => o.ref === value) ?? null;
  const triggerLabel =
    selectedOption?.label ??
    (value ? `${value} (unavailable)` : "Select model…");

  const close = useCallback(() => {
    setOpen(false);
    setActiveIndex(-1);
  }, []);

  const selectOption = useCallback(
    (ref: string) => {
      onChange(ref);
      close();
      buttonRef.current?.focus();
    },
    [onChange, close],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) {
      return;
    }
    const handler = (e: PointerEvent) => {
      if (
        containerRef.current &&
        !(e.target instanceof Node && containerRef.current.contains(e.target))
      ) {
        close();
      }
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, [open, close]);

  // Scroll active item into view
  useEffect(() => {
    if (!open || activeIndex < 0) {
      return;
    }
    const list = listRef.current;
    if (!list) {
      return;
    }
    const item = list.children[activeIndex];
    item?.scrollIntoView({ block: "nearest" });
  }, [open, activeIndex]);

  // Set initial active index when opening
  const handleToggle = useCallback(() => {
    if (open) {
      close();
    } else {
      const idx = options.findIndex((o) => o.ref === value);
      setActiveIndex(idx !== -1 ? idx : 0);
      setOpen(true);
    }
  }, [open, close, options, value]);

  const handleButtonKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === " ") {
        e.preventDefault();
        if (!open) {
          const idx = options.findIndex((o) => o.ref === value);
          setActiveIndex(idx !== -1 ? idx : 0);
          setOpen(true);
        }
      }
    },
    [open, options, value],
  );

  const handleListKeyDown = useCallback(
    (e: KeyboardEvent<HTMLUListElement>) => {
      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          setActiveIndex((i) => Math.min(i + 1, options.length - 1));
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          setActiveIndex((i) => Math.max(i - 1, 0));
          break;
        }
        case "Home": {
          e.preventDefault();
          setActiveIndex(0);
          break;
        }
        case "End": {
          e.preventDefault();
          setActiveIndex(options.length - 1);
          break;
        }
        case "Enter":
        case " ": {
          e.preventDefault();
          const activeOpt = options[activeIndex];
          if (activeOpt) {
            selectOption(activeOpt.ref);
          }
          break;
        }
        case "Escape":
        case "Tab": {
          close();
          buttonRef.current?.focus();
          break;
        }
        default: {
          break;
        }
      }
    },
    [activeIndex, options, selectOption, close],
  );

  const activeDescendantId =
    open && activeIndex >= 0 ? `${listboxId}-opt-${activeIndex}` : undefined;

  return (
    <div ref={containerRef} className={`relative ${className ?? ""}`}>
      {/* Trigger button */}
      <button
        ref={buttonRef}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={handleToggle}
        onKeyDown={handleButtonKeyDown}
        className="flex w-full items-center gap-2 border border-slate-200 bg-white px-3 py-1.5 text-left text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:focus:border-emerald-400/60 dark:focus:ring-emerald-400/30"
      >
        <ModelGlyph modelRef={value} size={18} />
        <span
          className={`flex-1 truncate font-mono ${!selectedOption && value ? "text-amber-600 dark:text-amber-400" : ""}`}
        >
          {triggerLabel}
        </span>
        {/* Chevron */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform dark:text-slate-500 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {/* Popover listbox */}
      {open && (
        <ul
          ref={listRef}
          id={listboxId}
          role="listbox"
          aria-activedescendant={activeDescendantId}
          onKeyDown={handleListKeyDown}
          tabIndex={-1}
          className="absolute left-0 right-0 top-full z-50 mt-0.5 max-h-64 overflow-y-auto border border-slate-200 bg-white shadow-lg outline-none dark:border-slate-700 dark:bg-slate-800"
        >
          {options.map((opt, i) => {
            const isSelected = opt.ref === value;
            const isActive = i === activeIndex;
            const { provider } = opt.ref
              ? classifyModelRef(opt.ref)
              : { provider: "unknown" as const };
            let rowBg = "hover:bg-slate-50 dark:hover:bg-slate-700/40";
            if (isActive) {
              rowBg = "bg-slate-100 dark:bg-slate-700/60";
            } else if (isSelected) {
              rowBg = "bg-emerald-50 dark:bg-emerald-500/10";
            }
            return (
              <li
                key={opt.ref || "__template_default__"}
                id={`${listboxId}-opt-${i}`}
                role="option"
                aria-selected={isSelected}
                onPointerDown={(e) => {
                  e.preventDefault();
                  selectOption(opt.ref);
                }}
                onPointerEnter={() => setActiveIndex(i)}
                className={[
                  "flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm",
                  rowBg,
                ].join(" ")}
              >
                <ModelGlyph modelRef={opt.ref} size={16} />
                <span className="flex-1 truncate font-mono text-slate-900 dark:text-white">
                  {opt.label}
                </span>
                {opt.ref && provider !== "unknown" && (
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    {provider}
                  </span>
                )}
                {isSelected && (
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-400"
                    aria-hidden="true"
                  >
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
